import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

type DemoAccount = {
  email: string;
  password: string;
  label: string;
  icon: string;
  /** ボタンの枠線・文字色 */
  color: string;
  /** ホバー時の枠線色 */
  hoverColor: string;
  /** ボタンの下に出す一行説明。何の店かが分かるようにする。 */
  description: string;
};

/**
 * デモアカウント。
 *
 * 押すとその業態の店に入れる。中身（商品・卓・従業員・14営業日ぶんの
 * 売上・勤怠・手当・未収金・ボトルキープ・日払い）まで入っているので、
 * 設定を1つも触らずに全画面を見て回れる。
 * データの投入は scripts/seed-nightlife-demo.js が作る。
 *
 * 美容室・クリニックは別アプリで扱うため、ここには置かない。
 */
const DEMO_ACCOUNTS: Record<string, DemoAccount> = {
  snack: {
    email: 'demo-snack@easymenu-demo.jp',
    password: 'EasyMenuDemo2026',
    label: 'スナック（8名）',
    description: '卓4・従業員8名。月115伝票なので無料の範囲',
    icon: '🥃',
    color: '#8C6239',
    hoverColor: '#6E4B2C',
  },
  menspub: {
    email: 'demo-menspub@easymenu-demo.jp',
    password: 'EasyMenuDemo2026',
    label: 'メンズパブ（30名）',
    description: '卓12・従業員30名。月326伝票でスタンダード。25日締め',
    icon: '🍸',
    color: '#C2185B',
    hoverColor: '#9C133F',
  },
  cabaret: {
    email: 'demo-cabaret@easymenu-demo.jp',
    password: 'EasyMenuDemo2026',
    label: 'キャバクラ（80名）',
    description: '卓30・従業員81名。月1,658伝票でプロ。朝5時まで営業',
    icon: '🍾',
    color: '#4A148C',
    hoverColor: '#38106B',
  },
  concafe: {
    email: 'demo-concafe@easymenu-demo.jp',
    password: 'EasyMenuDemo2026',
    label: 'コンカフェ（メイド）',
    description: '卓10・従業員15名。月287伝票。30分1,200円（18時から1,400円）。チェキとオリシャンあり',
    icon: '🎀',
    color: '#E91E63',
    hoverColor: '#C2185B',
  },
  izakaya: {
    email: 'demo-izakaya@easymenu-demo.jp',
    password: 'EasyMenuDemo2026',
    label: '居酒屋・ダイニングバー',
    description: '卓14・従業員10名。月417伝票。お客様のQR注文とホール入力の併用',
    icon: '🍶',
    color: '#D4442A',
    hoverColor: '#B8351F',
  },
};

/** #RRGGBB を rgba(r,g,b,alpha) に変換する(ホバー背景用) */
const withAlpha = (hex: string, alpha: number): string => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const demoTriggered = useRef(false);

  const handleLogin = async (loginEmail: string, loginPassword: string) => {
    setError(null);
    setLoading(true);
    try {
      await signIn(loginEmail, loginPassword);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'ログインに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleLogin(email, password);
  };

  const handleDemoLogin = async (demoType: string) => {
    const account = DEMO_ACCOUNTS[demoType];
    if (!account) return;
    await handleLogin(account.email, account.password);
  };

  // Auto-login from URL param ?demo=restaurant or ?demo=salon
  useEffect(() => {
    if (demoTriggered.current) return;
    const searchParams = new URLSearchParams(window.location.search);
    const demoType = searchParams.get('demo');
    if (demoType && DEMO_ACCOUNTS[demoType]) {
      demoTriggered.current = true;
      handleDemoLogin(demoType);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          店舗管理画面
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          ログインしてください
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="メールアドレス"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            autoComplete="email"
          />
          <TextField
            fullWidth
            label="パスワード"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            autoComplete="current-password"
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ mt: 3, mb: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'ログイン'}
          </Button>
        </form>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Link href="/register" underline="hover">
            新規店舗登録はこちら
          </Link>
        </Box>

        {/* Demo Login Section */}
        <Divider sx={{ my: 3 }}>デモ体験</Divider>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          押すとそのまま店の中に入れます。商品・卓・従業員・直近2週間の売上まで入っているので、
          設定を触らずに全部の画面を見て回れます。
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          {Object.entries(DEMO_ACCOUNTS).map(([key, account]) => (
            <Button
              key={key}
              variant="outlined"
              disabled={loading}
              onClick={() => handleDemoLogin(key)}
              sx={{
                textTransform: 'none',
                textAlign: 'left',
                alignItems: 'flex-start',
                flexDirection: 'column',
                py: 1.5,
                borderColor: account.color,
                color: account.color,
                '&:hover': {
                  borderColor: account.hoverColor,
                  backgroundColor: withAlpha(account.color, 0.04),
                },
              }}
            >
              <Typography variant="body1" fontWeight="bold" component="span">
                {account.icon} {account.label}
              </Typography>
              <Typography variant="caption" component="span"
                sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                {account.description}
              </Typography>
            </Button>
          ))}
        </Box>

        <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            お問い合わせ
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            ログイン情報がわからない場合や、新規導入のご相談は
          </Typography>
          <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
            株式会社ビーク
          </Typography>
          <Button
            variant="outlined"
            href="https://b19.co.jp"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mt: 1 }}
          >
            お問い合わせはこちら
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;
